import { Component, OnInit } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

import { LoaderComponent } from 'src/app/components/widgets/loader/loader.component';
import { ProjectFinanceService } from 'src/app/services/api/project-finance.service';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { CommonService } from 'src/app/services/common.service';
import { RouteService } from 'src/app/services/route.service';
import {
  PROJECT_FINANCE_DEFAULT_CURRENCY,
  ProjectFinanceExcelFile,
  ProjectPaymentsReportRow,
  projectInvoiceStatusClass,
  projectInvoiceStatusLabel
} from 'src/app/models/entities/project-finance';

@Component({
  selector: 'app-project-payments-report',
  imports: [ReactiveFormsModule, NgbTooltipModule, LoaderComponent, CurrencyPipe],
  templateUrl: './project-payments-report.component.html',
  styleUrl: './project-payments-report.component.scss'
})
export class ProjectPaymentsReportComponent implements OnInit {
  rows: ProjectPaymentsReportRow[] = [];
  isLoading = false;
  isExporting = false;

  readonly currency = PROJECT_FINANCE_DEFAULT_CURRENCY;
  readonly statusLabel = projectInvoiceStatusLabel;
  readonly statusClass = projectInvoiceStatusClass;
  readonly dateControl = new FormControl<string>('', { nonNullable: true });

  private readonly today: string;

  constructor(
    private projectFinanceService: ProjectFinanceService,
    private alertNotificationService: AlertNotificationService,
    private commonService: CommonService,
    private routeService: RouteService
  ) {
    this.today = this.commonService.formatDateOnly(new Date());
  }

  ngOnInit(): void {
    this.dateControl.setValue(this.today);
    this.dateControl.valueChanges.subscribe(date => {
      if (date) this.loadReport();
    });
    this.loadReport();
  }

  get isToday(): boolean {
    return this.dateControl.value === this.today;
  }

  shiftDate(days: number): void {
    const current = new Date(`${this.dateControl.value || this.today}T00:00:00`);
    current.setDate(current.getDate() + days);
    this.dateControl.setValue(this.commonService.formatDateOnly(current));
  }

  goToToday(): void {
    this.dateControl.setValue(this.today);
  }

  formatDate(value: string | null | undefined, withTime: boolean = false): string {
    return this.commonService.formatDateValue(value, withTime);
  }

  isLate(row: ProjectPaymentsReportRow): boolean {
    return row.status === 'OVERDUE' || row.status === 'LOCKED';
  }

  openProjectFinance(row: ProjectPaymentsReportRow): void {
    this.routeService.projectsFinanceIncome(row.projectId);
  }

  exportExcel(): void {
    this.isExporting = true;
    this.projectFinanceService.exportProjectPaymentsReportExcel(this.dateControl.value).subscribe({
      next: (response) => {
        this.isExporting = false;
        const file = response?.data as ProjectFinanceExcelFile | undefined;
        if (!file?.base64) {
          this.alertNotificationService.error('Unable to download file: missing base64 data');
          return;
        }
        this.commonService.downloadBase64File(file.base64, file.fileName, file.mimeType);
        this.alertNotificationService.success('File Excel downloaded successfully');
      },
      error: (error) => {
        this.isExporting = false;
        console.error('Error exporting project payments report:', error);
        this.alertNotificationService.error('Error downloading Excel file');
      }
    });
  }

  private loadReport(): void {
    this.isLoading = true;
    this.projectFinanceService.listProjectPaymentsReport(this.dateControl.value).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.rows = this.projectFinanceService.readResponseKey<ProjectPaymentsReportRow[]>(response, 'rows') ?? [];
      },
      error: (error) => {
        this.isLoading = false;
        this.rows = [];
        console.error('Error loading project payments report:', error);
        this.alertNotificationService.error(this.projectFinanceService.errorMessage(error, 'Error loading project payments report'));
      }
    });
  }
}
